import { isProduction, logger } from '@web_opxp/features';
import { IHostedPageData } from './types';

const chargebeeJsSrc = 'https://js.chargebee.com/v2/chargebee.js';
const sdkId = 'chargebee-js-sdk';

export const CHARGEBEE_SITE = isProduction() ? 'neuronation' : 'neuronation-test';

export interface ICheckoutCallbacks {
  loaded?: () => void;
  error?: () => void;
  success?: (hostedPageId: string) => void;
  close?: () => void;
  step?: (currentStep: string) => void;
  resize?: (pageHeight: number) => void;
}

interface IChargebeeCheckoutOptions extends ICheckoutCallbacks {
  hostedPage: () => Promise<IHostedPageData>;
  layout?: 'in_app' | 'full_page';
}

interface IChargebeeInstance {
  openCheckout: (options: IChargebeeCheckoutOptions) => void;
  setCheckoutCallbacks: (setterFunction: (cart: unknown) => ICheckoutCallbacks) => void;
}

interface IChargebeeInitOptions {
  site: string;
  enableGATracking: boolean;
  iframeOnly: boolean;
  enableRedirectMode?: boolean;
}

interface IChargebee {
  init: (options: IChargebeeInitOptions) => IChargebeeInstance;
  getInstance: () => IChargebeeInstance;
}

declare const Chargebee: IChargebee;

function getService() {
  let instance: IChargebeeInstance;
  let loaded = false;
  let finished = false;
  function init(enableGATracking: boolean) {
    return new Promise((resolve, reject) => {
      if (document.getElementById(sdkId)) {
        if (!instance) {
          instance = Chargebee.getInstance();
        }
        resolve(instance);
        return;
      }
      function onload() {
        loaded = true;
        instance = Chargebee.init({
          site: CHARGEBEE_SITE,
          enableGATracking,
          enableRedirectMode: true,
          iframeOnly: true,
        });
        resolve(instance);
      }
      function onerror() {
        reject(new Error('Failed to load the Chargebee service necessary to process the payment!'));
      }
      const script = document.createElement('script');
      script.id = sdkId;
      script.src = chargebeeJsSrc;
      script.onload = onload;
      script.onerror = onerror;
      const scripts = document.getElementsByTagName('script');
      const firstScript = scripts[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    });
  }
  function openCheckout(
    hostedPageData: IHostedPageData,
    success?: (pageId: string) => void,
    close?: () => void,
    step?: (step: string) => void,
    error?: () => void
  ) {
    if (!instance) {
      if (!loaded) {
        throw new Error('Chargebee service must be initialized first!');
      }
      instance = Chargebee.getInstance();
    }
    finished = false;
    function hostedPage() {
      return Promise.resolve(hostedPageData);
    }
    function onSuccess(pageId: string) {
      finished = true;
      if (!success) {
        return;
      }
      success(pageId);
    }
    function onClose() {
      if (finished) {
        return;
      }
      if (!close) {
        return;
      }
      close();
    }
    function onError() {
      if (finished) {
        return;
      }
      if (!error) {
        return;
      }
      error();
    }
    instance.openCheckout({
      hostedPage,
      close: onClose,
      step,
      success: onSuccess,
      error: onError,
    });
  }
  function close() {
    return new Promise((resolve, reject) => {
      if (!instance) {
        resolve(undefined);
        return;
      }
      const script = document.getElementById(sdkId);
      if (!script || script === null) {
        resolve(undefined);
        return;
      }
      try {
        script.remove();
      } catch {
        const parent = script.parentNode;
        if (!parent) {
          reject(new Error('Error by removing Service script from the document'));
          return;
        }
        parent.removeChild(script);
      }
      resolve(undefined);
    });
  }
  function setupCallbacks(
    success?: (pageId: string) => void,
    close?: () => void,
    step?: (step: string) => void,
    error?: () => void
  ) {
    if (!instance) {
      if (!loaded) {
        throw new Error('Chargebee service must be initialized first!');
      }
      instance = Chargebee.getInstance();
    }
    finished = false;
    function onSuccess(pageId: string) {
      finished = true;
      if (!success) {
        return;
      }
      success(pageId);
    }
    function onClose() {
      if (finished) {
        return;
      }
      if (!close) {
        return;
      }
      close();
    }
    function onError() {
      if (finished) {
        return;
      }
      if (!error) {
        return;
      }
      error();
    }
    instance.setCheckoutCallbacks((cart) => {
      logger([`setting callbacks for cart`, cart]);
      return {
        close: onClose,
        step,
        success: onSuccess,
        error: onError,
      };
    });
  }
  return {
    init,
    loaded,
    openCheckout,
    close,
    setupCallbacks,
  };
}

const ChargebeeService = getService();
export default ChargebeeService;
